
import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { PageHeader, LoadingState, ErrorState, EmptyState } from "@/components/features/states";
import { useUiStore } from "@/store/ui";
import { formatPrice, formatRelativeTime } from "@/lib/format";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Users, Package, ShieldCheck, Flag, Banknote, Clock, ArrowRight,
  CheckCircle, Megaphone, AlertTriangle, Percent, Mail, MapPin, Droplets,
  Store, Truck, Navigation, Activity, Bike, XCircle,
} from "lucide-react";
import type { DeliveryJob, Dispute, RoleApplication, Store as StoreType, JobStatus } from "@/lib/types";

const ACTIVE_JOB_STATUSES: JobStatus[] = ["assigned", "picked_up", "in_transit"];
const PENDING_JOB_STATUSES: JobStatus[] = ["open", "negotiating"];

// Stable string hash for deterministic mock positions on the fleet map.
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// Derive a stable mock position (percent of map width/height) from a seed.
function fleetPosition(seed: string): { x: number; y: number } {
  const h = hashString(seed);
  const x = 8 + (h % 84);
  const y = 14 + ((h >> 7) % 72);
  return { x, y };
}

// ---------------------------------------------------------------------------
// Admin Dashboard — operations overview.
// Composes 5 widgets + the legacy status/category charts:
//   1. Global KPI hero bar (Orders / Couriers / Avg time / GMV)
//   2. Legacy KPI grid (Users / Jobs / Completed / ID pending / Disputes / Revenue)
//      + Phase 2 grid (Commission / Ads / Fraud / Emails)
//   3. Live Fleet Map
//   4. Launch Partner (Water) Inventory Tracker
//   5. Merchant Approval Queue
//   6. Dispute Resolution Center
//   7. Jobs by status bar chart + Jobs by category chips (kept)
// ---------------------------------------------------------------------------
export function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useUiStore((s) => s.navigate);
  const qc = useQueryClient();

  // Stats — top KPIs (15s polling).
  const statsQuery = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => api.admin.stats(),
    refetchInterval: 15000,
  });
  // Jobs — fleet map (10s polling for live feel).
  const jobsQuery = useQuery({
    queryKey: ["admin", "jobs"],
    queryFn: () => api.admin.jobs(),
    refetchInterval: 10000,
  });
  // Pending merchant applications (30s polling).
  const roleAppsQuery = useQuery({
    queryKey: ["admin", "role-applications", "pending"],
    queryFn: () => api.admin.roleApplications({ status: "pending" }),
    refetchInterval: 30000,
  });
  // Disputes (30s polling).
  const disputesQuery = useQuery({
    queryKey: ["admin", "disputes"],
    queryFn: () => api.admin.disputes(),
    refetchInterval: 30000,
  });
  // Approved stores — water inventory tracker (30s polling).
  const storesQuery = useQuery({
    queryKey: ["admin", "stores", "approved"],
    queryFn: () => api.admin.stores({ status: "approved" }),
    refetchInterval: 30000,
  });

  // The stats endpoint returns `any` per the API contract — safe to index.
  const stats: any = statsQuery.data?.data?.stats;
  const jobs: DeliveryJob[] = jobsQuery.data?.data?.jobs ?? [];
  const applications: RoleApplication[] = roleAppsQuery.data?.data?.applications ?? [];
  const disputes: Dispute[] = disputesQuery.data?.data?.items ?? [];
  const stores: StoreType[] = storesQuery.data?.data?.stores ?? [];

  // Active couriers — distinct providerId in active jobs.
  const activeCourierIds = useMemo(() => {
    const ids = new Set<string>();
    jobs.forEach((j) => {
      if (j.providerId && ACTIVE_JOB_STATUSES.includes(j.status)) ids.add(j.providerId);
    });
    return Array.from(ids);
  }, [jobs]);

  // Avg delivery time — compute from completed jobs (fallback: 24 min).
  const avgDeliveryMinutes = useMemo(() => {
    const completed = jobs.filter(
      (j) => (j.status === "delivered" || j.status === "completed") && j.assignedAt && j.deliveredAt
    );
    if (completed.length === 0) return 24;
    const totalMin = completed.reduce((sum, j) => {
      const mins =
        (new Date(j.deliveredAt as string).getTime() - new Date(j.assignedAt as string).getTime()) / 60000;
      return sum + (mins > 0 && mins < 600 ? mins : 24);
    }, 0);
    return Math.round(totalMin / completed.length);
  }, [jobs]);

  // Approve/reject mutation for the merchant queue.
  const reviewMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { action: "approve" | "reject"; reason?: string } }) =>
      api.admin.reviewRoleApplication(id, body),
    onSuccess: (_res, vars) => {
      toast.success(
        vars.body.action === "approve"
          ? t("admin.dashboard.applicationApproved")
          : t("admin.dashboard.applicationRejected")
      );
      qc.invalidateQueries({ queryKey: ["admin", "role-applications"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : t("admin.dashboard.reviewApplicationFailed")),
  });

  if (statsQuery.isLoading) return <LoadingState />;
  if (statsQuery.error || !statsQuery.data?.ok)
    return (
      <ErrorState
        message={statsQuery.data?.error || t("admin.dashboard.loadFailed")}
        onRetry={statsQuery.refetch}
      />
    );
  if (!stats) return null;

  const statusBreakdown: { status: string; count: number }[] = Object.entries(
    stats.jobsByStatus || {}
  ).map(([status, count]) => ({ status, count: count as number }));

  return (
    <div className="space-y-5">
      <PageHeader title={t("admin.dashboard.pageTitle")} subtitle={t("admin.dashboard.operationsSubtitle")} />

      {/* 1. Global KPI hero bar */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <HeroKpi
          icon={Package}
          label={t("admin.dashboard.heroOrdersToday")}
          value={stats.totalJobs}
          hint={t("admin.dashboard.heroCompletedHint", { count: stats.completedJobs })}
          color="text-primary"
          onClick={() => navigate("admin/jobs")}
        />
        <HeroKpi
          icon={Bike}
          label={t("admin.dashboard.heroActiveCouriers")}
          value={activeCourierIds.length}
          hint={t("admin.dashboard.heroOnRoadNow")}
          color="text-emerald-600"
          onClick={() => navigate("admin/users")}
        />
        <HeroKpi
          icon={Clock}
          label={t("admin.dashboard.heroAvgDeliveryTime")}
          value={t("admin.dashboard.heroAvgDeliveryValue", { minutes: avgDeliveryMinutes })}
          hint={t("admin.dashboard.heroLast24h")}
          color="text-amber-600"
          onClick={() => navigate("admin/analytics")}
        />
        <HeroKpi
          icon={Banknote}
          label={t("admin.dashboard.heroPlatformRevenue")}
          value={formatPrice(stats.totalRevenue || 0)}
          hint={t("admin.dashboard.heroCommissionHint", { amount: formatPrice(stats.totalCommission || 0) })}
          color="text-gold"
          onClick={() => navigate("admin/commission-rules")}
        />
      </div>

      {/* Legacy KPI grid (kept) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi icon={Users} label={t("admin.dashboard.usersShort")} value={stats.totalUsers} color="text-sky-600" onClick={() => navigate("admin/users")} />
        <Kpi icon={Package} label={t("admin.dashboard.jobsLabel")} value={stats.totalJobs} color="text-primary" onClick={() => navigate("admin/jobs")} />
        <Kpi icon={CheckCircle} label={t("admin.dashboard.completedLabel")} value={stats.completedJobs} color="text-emerald-600" />
        <Kpi icon={ShieldCheck} label={t("admin.dashboard.idPending")} value={stats.pendingVerifications} color="text-amber-600" onClick={() => navigate("admin/verifications")} />
        <Kpi icon={Flag} label={t("admin.dashboard.openDisputes")} value={stats.openDisputes} color="text-red-600" onClick={() => navigate("admin/disputes")} />
        <Kpi icon={Banknote} label={t("admin.dashboard.revenueShort")} value={formatPrice(stats.totalRevenue || 0)} color="text-gold" />
      </div>

      {/* Phase 2 KPI grid (kept) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi icon={Percent} label={t("admin.dashboard.commission")} value={formatPrice(stats.totalCommission || 0)} color="text-gold" onClick={() => navigate("admin/commission-rules")} />
        <Kpi icon={Megaphone} label={t("admin.dashboard.activeAds")} value={stats.activeAds ?? 0} color="text-amber-600" onClick={() => navigate("admin/ads")} />
        <Kpi icon={AlertTriangle} label={t("admin.dashboard.fraudFlags")} value={stats.openFraudFlags ?? 0} color="text-red-600" onClick={() => navigate("admin/fraud-flags")} />
        <Kpi icon={Mail} label={t("admin.dashboard.emailsSent")} value={stats.emailLogsCount ?? 0} color="text-sky-600" onClick={() => navigate("admin/analytics")} />
      </div>

      {/* 2. Live Fleet Map */}
      <FleetMap jobs={jobs} />

      {/* 3 & 4. Water Inventory + Merchant Approval Queue (side-by-side on lg) */}
      <div className="grid gap-5 lg:grid-cols-2">
        <WaterInventoryTracker stores={stores} jobs={jobs} />
        <MerchantApprovalQueue
          applications={applications}
          onReview={(id, body) => reviewMutation.mutate({ id, body })}
          loading={reviewMutation.isPending}
          onViewAll={() => navigate("admin/role-applications")}
        />
      </div>

      {/* 5. Dispute Resolution Center */}
      <DisputeResolutionCenter
        disputes={disputes}
        onViewAll={() => navigate("admin/disputes")}
      />

      {/* Jobs by status bar chart (kept) */}
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 text-sm font-semibold">{t("admin.dashboard.jobsByStatus")}</h3>
          {statusBreakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("admin.dashboard.noJobsYet")}</p>
          ) : (
            <div className="space-y-2">
              {statusBreakdown.map((s) => {
                const max = Math.max(...statusBreakdown.map((x) => x.count), 1);
                return (
                  <div key={s.status} className="flex items-center gap-3">
                    <span className="w-28 text-xs capitalize text-muted-foreground">
                      {s.status.replace("_", " ")}
                    </span>
                    <div className="h-6 flex-1 overflow-hidden rounded bg-muted">
                      <div
                        className="flex h-full items-center justify-end rounded bg-primary px-2 text-[10px] font-bold text-primary-foreground"
                        style={{ width: `${(s.count / max) * 100}%` }}
                      >
                        {s.count}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Jobs by category chips (kept) */}
      {stats.jobsByCategory && Object.keys(stats.jobsByCategory).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="mb-3 text-sm font-semibold">{t("admin.dashboard.jobsByCategory")}</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.jobsByCategory).map(([cat, count]) => (
                <span
                  key={cat}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium capitalize"
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      cat === "food"
                        ? "bg-amber-500"
                        : cat === "grocery"
                          ? "bg-emerald-500"
                          : cat === "pharmacy"
                            ? "bg-rose-500"
                            : cat === "water"
                              ? "bg-sky-500"
                              : "bg-primary"
                    }`}
                  />
                  {cat}: <span className="font-bold">{count as number}</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick actions (reorganized) */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <ActionCard
          icon={ShieldCheck}
          title={t("admin.dashboard.idVerifications")}
          desc={t("admin.dashboard.pendingReviewCount", { count: stats.pendingVerifications })}
          onClick={() => navigate("admin/verifications")}
        />
        <ActionCard
          icon={Flag}
          title={t("admin.dashboard.disputes")}
          desc={t("admin.dashboard.openDisputesCount", { count: stats.openDisputes })}
          onClick={() => navigate("admin/disputes")}
        />
        <ActionCard
          icon={Package}
          title={t("admin.dashboard.allJobs")}
          desc={t("admin.dashboard.totalJobsCount", { count: stats.totalJobs })}
          onClick={() => navigate("admin/jobs")}
        />
        <ActionCard
          icon={Store}
          title={t("admin.dashboard.storesAction")}
          desc={t("admin.dashboard.manageMerchants")}
          onClick={() => navigate("admin/stores")}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero KPI card — larger, prominent (top of dashboard).
// ---------------------------------------------------------------------------
export function HeroKpi({
  icon: Icon,
  label,
  value,
  hint,
  color,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  hint?: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <Card
      onClick={onClick}
      className={
        onClick
          ? "cursor-pointer overflow-hidden transition hover:border-primary/40 hover:shadow-sm"
          : "overflow-hidden"
      }
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {label}
            </p>
            <p className="mt-1 truncate text-2xl font-bold leading-tight">{value}</p>
            {hint && <p className="mt-0.5 truncate text-xs text-muted-foreground">{hint}</p>}
          </div>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Compact KPI card — used in the dense grids below the hero bar.
export function Kpi({
  icon: Icon,
  label,
  value,
  color,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  color: string;
  onClick?: () => void;
}) {
  return (
    <Card onClick={onClick} className={onClick ? "cursor-pointer transition hover:border-primary/40" : ""}>
      <CardContent className="flex flex-col items-center gap-1 p-3 text-center">
        <Icon className={`h-5 w-5 ${color}`} />
        <p className="text-lg font-bold leading-tight">{value}</p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

export function ActionCard({
  icon: Icon,
  title,
  desc,
  onClick,
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <Card onClick={onClick} className="cursor-pointer transition hover:border-primary/40 hover:shadow-sm">
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="font-semibold">{title}</p>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 2. Live Fleet Map — stylized visualization of active couriers.
// Derives mock positions from api.admin.jobs() using a stable hash of job IDs.
// ---------------------------------------------------------------------------
export function FleetMap({ jobs }: { jobs: DeliveryJob[] }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);

  const activeJobs = useMemo(
    () => jobs.filter((j) => ACTIVE_JOB_STATUSES.includes(j.status)),
    [jobs]
  );
  const pendingJobs = useMemo(
    () => jobs.filter((j) => PENDING_JOB_STATUSES.includes(j.status)),
    [jobs]
  );
  const inTransitJobs = useMemo(() => jobs.filter((j) => j.status === "in_transit"), [jobs]);

  const selectedJob = selected ? activeJobs.find((j) => j.id === selected) ?? null : null;
  const isEmpty = activeJobs.length === 0 && pendingJobs.length === 0;

  // Clamp popup position so it never overflows the map edges.
  const popupPos = selectedJob ? fleetPosition(selectedJob.id) : null;
  const popupStyle = popupPos
    ? {
        left: `${Math.min(Math.max(popupPos.x, 28), 72)}%`,
        top: `${Math.max(popupPos.y - 6, 18)}%`,
      }
    : undefined;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2 py-0.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
              {t("admin.dashboard.liveBadge")}
            </span>
          </span>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Navigation className="h-4 w-4 text-primary" />
            {t("admin.dashboard.liveFleetMap")}
          </CardTitle>
        </div>
        <div className="hidden flex-wrap items-center gap-3 text-[10px] text-muted-foreground sm:flex">
          <LegendItem color="bg-emerald-500" shape="dot" label={t("admin.dashboard.legendActiveCourier")} />
          <LegendItem color="bg-amber-500" shape="dot" label={t("admin.dashboard.legendPendingPickup")} />
          <LegendItem color="bg-sky-500" shape="line" label={t("admin.dashboard.legendDeliveryInProgress")} />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative h-72 w-full overflow-hidden bg-slate-900 dark:bg-black sm:h-80">
          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "linear-gradient(rgba(16,185,129,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.35) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />
          {/* Radial glow */}
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(16,185,129,0.15),transparent_70%)]" />

          {isEmpty ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
              <Navigation className="h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">{t("admin.dashboard.noActiveDeliveries")}</p>
            </div>
          ) : (
            <>
              {/* Delivery paths (in_transit) — dashed sky-blue lines */}
              <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none">
                {inTransitJobs.map((j) => {
                  const start = fleetPosition(j.id);
                  const end = fleetPosition(j.id + "-drop");
                  return (
                    <line
                      key={`path-${j.id}`}
                      x1={`${start.x}%`}
                      y1={`${start.y}%`}
                      x2={`${end.x}%`}
                      y2={`${end.y}%`}
                      stroke="rgba(56,189,248,0.65)"
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                    />
                  );
                })}
              </svg>

              {/* Pending pickup pins (orange) */}
              {pendingJobs.slice(0, 12).map((j) => {
                const pos = fleetPosition(j.id + "-pickup");
                return (
                  <div
                    key={`pending-${j.id}`}
                    className="absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                    title={t("admin.dashboard.pendingTitleHint", { code: j.code })}
                  >
                    <span className="block h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-amber-500/30" />
                  </div>
                );
              })}

              {/* Active couriers (green, pulsing) */}
              {activeJobs.map((j) => {
                const pos = fleetPosition(j.id);
                const isSelected = selected === j.id;
                return (
                  <button
                    key={`active-${j.id}`}
                    type="button"
                    onClick={() => setSelected(isSelected ? null : j.id)}
                    className="absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                    aria-label={t("admin.dashboard.courierAriaLabel", { name: j.provider?.name ?? t("admin.fraudFlags.unknownUser") })}
                  >
                    <span className="relative flex h-3.5 w-3.5 items-center justify-center">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
                      <span
                        className={`relative inline-flex h-3 w-3 rounded-full ring-2 ring-emerald-500/40 ${
                          isSelected ? "bg-emerald-300 ring-white" : "bg-emerald-500"
                        }`}
                      />
                    </span>
                  </button>
                );
              })}

              {/* Selected courier popup */}
              {selectedJob && popupStyle && (
                <div
                  className="absolute z-10 w-56 -translate-x-1/2 -translate-y-full"
                  style={popupStyle}
                >
                  <div className="rounded-lg border border-emerald-500/30 bg-slate-800/95 p-3 text-white shadow-xl backdrop-blur dark:bg-slate-900/95">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-bold">{selectedJob.provider?.name ?? t("admin.dashboard.legendActiveCourier")}</p>
                      <button
                        type="button"
                        onClick={() => setSelected(null)}
                        className="text-muted-foreground hover:text-white"
                        aria-label={t("admin.dashboard.closeAriaLabel")}
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mt-1.5 space-y-0.5 text-[11px] text-slate-300">
                      <p className="flex items-center gap-1">
                        <Truck className="h-3 w-3" /> {selectedJob.code} ·{" "}
                        <span className="capitalize">{selectedJob.status.replace("_", " ")}</span>
                      </p>
                      <p className="flex items-center gap-1">
                        <Activity className="h-3 w-3" /> {t("admin.dashboard.speedKmh", { speed: 20 + (hashString(selectedJob.id) % 25) })}
                      </p>
                      <p className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{" "}
                        {selectedJob.dropoffAddress.slice(0, 32) || "—"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Mobile legend (compact) */}
          <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-2 text-[10px] text-slate-300 sm:hidden">
            <LegendItem color="bg-emerald-500" shape="dot" label={t("admin.dashboard.legendActiveShort")} />
            <LegendItem color="bg-amber-500" shape="dot" label={t("admin.dashboard.legendPendingShort")} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LegendItem({
  color,
  shape,
  label,
}: {
  color: string;
  shape: "dot" | "line";
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      {shape === "dot" ? (
        <span className={`h-2 w-2 rounded-full ${color}`} />
      ) : (
        <span className={`inline-block h-0.5 w-4 ${color}`} />
      )}
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 3. Launch Partner (Water) Inventory Tracker.
// Filters approved stores whose name/category indicates drinking water.
// ---------------------------------------------------------------------------
export function WaterInventoryTracker({
  stores,
  jobs,
}: {
  stores: StoreType[];
  jobs: DeliveryJob[];
}) {
  const { t } = useTranslation();
  const waterStores = useMemo(
    () =>
      stores.filter((s) => {
        const name = s.name.toLowerCase();
        return (
          s.category === "convenience" ||
          name.includes("water") ||
          name.includes("ahla")
        );
      }),
    [stores]
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Droplets className="h-4 w-4 text-sky-500" />
          {t("admin.dashboard.waterInventory")}
          <Badge variant="secondary" className="ml-auto text-[10px]">
            {t("admin.dashboard.waterStoresCount", { count: waterStores.length })}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {waterStores.length === 0 ? (
          <EmptyState
            icon={<Droplets className="h-8 w-8" />}
            title={t("admin.dashboard.noWaterProviders")}
            description={t("admin.dashboard.noWaterProvidersDesc")}
          />
        ) : (
          waterStores.map((s) => <WaterStoreRow key={s.id} store={s} jobs={jobs} />)
        )}
      </CardContent>
    </Card>
  );
}

function WaterStoreRow({ store, jobs }: { store: StoreType; jobs: DeliveryJob[] }) {
  const { t } = useTranslation();
  // Mock stock — derived deterministically from store.id hash (or products count).
  const baseStock = useMemo(() => {
    const productCount = store.products?.length ?? 0;
    if (productCount > 0) return productCount * 8;
    return 5 + (hashString(store.id) % 40);
  }, [store.id, store.products]);

  const [stock, setStock] = useState<number>(baseStock);
  const [input, setInput] = useState<string>(String(baseStock));

  const pendingOrders = useMemo(
    () =>
      jobs.filter(
        (j) => j.storeId === store.id && (j.status === "open" || j.status === "assigned")
      ).length,
    [jobs, store.id]
  );

  const lowStock = stock < 10;

  const handleUpdate = () => {
    const n = parseInt(input, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error(t("admin.dashboard.waterStockInvalid"));
      return;
    }
    setStock(n);
    toast.success(t("admin.dashboard.waterStockUpdated", { name: store.name, units: n }));
  };

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{store.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {store.address || t("admin.dashboard.waterNoAddress")} · ★ {store.rating.toFixed(1)} ({store.ratingCount})
          </p>
        </div>
        <Badge variant={lowStock ? "destructive" : "secondary"} className="shrink-0 text-[10px]">
          {lowStock ? t("admin.dashboard.waterLowStock") : t("admin.dashboard.waterInStock")}
        </Badge>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-md bg-muted/40 p-1.5">
          <p className="text-muted-foreground">{t("admin.dashboard.waterStockLabel")}</p>
          <p className={`text-base font-bold ${lowStock ? "text-red-600" : "text-foreground"}`}>
            {stock}
          </p>
        </div>
        <div className="rounded-md bg-muted/40 p-1.5">
          <p className="text-muted-foreground">{t("admin.dashboard.waterPendingLabel")}</p>
          <p className="text-base font-bold text-amber-600">{pendingOrders}</p>
        </div>
        <div className="rounded-md bg-muted/40 p-1.5">
          <p className="text-muted-foreground">{t("admin.dashboard.waterCommLabel")}</p>
          <p className="text-base font-bold text-gold">{store.commissionRate}%</p>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <Input
          type="number"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="h-8 text-xs"
          min={0}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 shrink-0 text-xs"
          onClick={handleUpdate}
        >
          {t("admin.dashboard.waterUpdate")}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Merchant Approval Queue — pending provider/courier applications.
// ---------------------------------------------------------------------------
export function MerchantApprovalQueue({
  applications,
  onReview,
  loading,
  onViewAll,
}: {
  applications: RoleApplication[];
  onReview: (id: string, body: { action: "approve" | "reject"; reason?: string }) => void;
  loading: boolean;
  onViewAll: () => void;
}) {
  const { t } = useTranslation();
  const items = applications.slice(0, 5);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-2">
            <Store className="h-4 w-4 text-primary" />
            {t("admin.dashboard.merchantApprovalQueue")}
          </span>
          <Badge variant="secondary" className="text-[10px]">
            {t("admin.dashboard.merchantPendingCount", { count: applications.length })}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <EmptyState
            icon={<CheckCircle className="h-8 w-8" />}
            title={t("admin.dashboard.noPendingApplications")}
            description={t("admin.dashboard.noPendingApplicationsDesc")}
          />
        ) : (
          items.map((app) => {
            const isProvider = app.role === "provider";
            const Icon = isProvider ? Store : Bike;
            return (
              <div key={app.id} className="rounded-lg border border-border p-3">
                <div className="flex items-start gap-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold">
                        {isProvider
                          ? app.businessName || t("admin.dashboard.unknownBusiness")
                          : t("admin.dashboard.licenseLabel", { number: app.licenseNumber || "—" })}
                      </p>
                      <Badge variant="outline" className="shrink-0 text-[10px] capitalize">
                        {app.role}
                      </Badge>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {app.user?.name ?? t("admin.fraudFlags.unknownUser")} · {app.user?.email ?? "—"}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {t("admin.dashboard.submittedAgo", { time: formatRelativeTime(app.submittedAt) })}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex gap-1.5">
                  <Button
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    disabled={loading}
                    onClick={() => onReview(app.id, { action: "approve" })}
                  >
                    <CheckCircle className="mr-1 h-3.5 w-3.5" /> {t("admin.dashboard.verifyAndActivate")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 flex-1 text-xs"
                    disabled={loading}
                    onClick={() => onReview(app.id, { action: "reject" })}
                  >
                    <XCircle className="mr-1 h-3.5 w-3.5" /> {t("adminRoleApplications.reject")}
                  </Button>
                </div>
              </div>
            );
          })
        )}
        {applications.length > 5 && (
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onViewAll}>
            {t("admin.dashboard.viewAllCount", { count: applications.length })} <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 5. Dispute Resolution Center — flagged orders with inline resolve action.
// ---------------------------------------------------------------------------
export function DisputeResolutionCenter({
  disputes,
  onViewAll,
}: {
  disputes: Dispute[];
  onViewAll: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [resolutions, setResolutions] = useState<Record<string, string>>({});

  const resolveMutation = useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: string }) =>
      api.admin.resolveDispute(id, { resolution, finalStatus: "resolved" }),
    onSuccess: () => {
      toast.success(t("admin.dashboard.disputeResolvedToast"));
      qc.invalidateQueries({ queryKey: ["admin", "disputes"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : t("admin.dashboard.disputeResolveFailed")),
  });

  const openDisputes = useMemo(
    () => disputes.filter((d) => d.status !== "resolved" && d.status !== "closed"),
    [disputes]
  );
  const items = openDisputes.slice(0, 5);

  const statusColor = (s: string): string => {
    if (s === "open") return "bg-red-500/10 text-red-600";
    if (s === "investigating") return "bg-amber-500/10 text-amber-600";
    if (s === "resolved") return "bg-emerald-500/10 text-emerald-600";
    return "bg-muted text-muted-foreground";
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-red-600" />
            {t("admin.dashboard.disputeResolutionCenter")}
          </span>
          <Badge variant="secondary" className="text-[10px]">
            {t("admin.dashboard.disputeOpenCount", { count: openDisputes.length })}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <EmptyState
            icon={<Flag className="h-8 w-8" />}
            title={t("admin.dashboard.noOpenDisputes")}
            description={t("admin.dashboard.noOpenDisputesDesc")}
          />
        ) : (
          items.map((d) => {
            const text = resolutions[d.id] ?? "";
            return (
              <div key={d.id} className="rounded-lg border border-border p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={`capitalize border-none ${statusColor(d.status)}`}
                      >
                        {d.status}
                      </Badge>
                      <p className="truncate text-sm font-semibold">
                        {d.reason.replace(/_/g, " ")}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      <span className="font-mono">{d.job?.code ?? "—"}</span>
                      {" · "}{t("admin.dashboard.raisedByLabel", { name: d.raisedBy?.name ?? t("admin.fraudFlags.unknownUser") })}{" · "}
                      {formatRelativeTime(d.createdAt)}
                    </p>
                  </div>
                </div>
                {d.description && (
                  <p className="mt-2 rounded-md bg-muted/40 p-2 text-xs">{d.description}</p>
                )}
                <div className="mt-2 flex gap-1.5">
                  <Input
                    value={text}
                    onChange={(e) =>
                      setResolutions((prev) => ({ ...prev, [d.id]: e.target.value }))
                    }
                    placeholder={t("admin.dashboard.resolutionNotesPlaceholder")}
                    className="h-8 text-xs"
                  />
                  <Button
                    size="sm"
                    className="h-8 shrink-0 text-xs"
                    disabled={!text.trim() || resolveMutation.isPending}
                    onClick={() =>
                      resolveMutation.mutate({ id: d.id, resolution: text.trim() })
                    }
                  >
                    <CheckCircle className="mr-1 h-3.5 w-3.5" /> {t("admin.dashboard.disputeResolveButton")}
                  </Button>
                </div>
              </div>
            );
          })
        )}
        {openDisputes.length > 5 && (
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={onViewAll}>
            {t("admin.dashboard.viewAllCount", { count: openDisputes.length })} <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
